import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, User, LogIn, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { BRAND } from "@/lib/brand";

export default function AdminLoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/login", { username, password });
      // Auth cookie is set by the server; nothing to store client-side.
      toast.success("Welcome back!");
      navigate("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--brand-teal)] p-6" data-testid="admin-login-page">
      <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, rgba(201,169,106,0.4), transparent 50%), radial-gradient(circle at 80% 70%, rgba(201,169,106,0.3), transparent 50%)" }} />

      <div className="relative w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm mb-6" data-testid="admin-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to site
        </Link>
        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-10">
          <div className="text-center mb-8">
            <img src={BRAND.logoUrl} alt="Logo" className="h-16 w-16 mx-auto mb-4 object-contain" />
            <h1 className="text-2xl font-extrabold text-[var(--brand-teal)]" style={{ fontFamily: "Outfit" }}>Admin Portal</h1>
            <p className="text-sm text-slate-500 mt-1">Top-Notch Pro Solutions • Staff Only</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-[var(--brand-teal)] mb-2 uppercase tracking-wider">Username</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoFocus
                  className="w-full pl-10 pr-3 py-3 border-2 border-[#dcd5c6] rounded-lg focus:outline-none focus:border-[var(--brand-teal)] transition"
                  placeholder="Enter your username"
                  data-testid="admin-username-input"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[var(--brand-teal)] mb-2 uppercase tracking-wider">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full pl-10 pr-3 py-3 border-2 border-[#dcd5c6] rounded-lg focus:outline-none focus:border-[var(--brand-teal)] transition"
                  placeholder="••••••••"
                  data-testid="admin-password-input"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full justify-center mt-2"
              data-testid="admin-login-btn"
            >
              {loading ? "Signing in..." : <>Sign In <LogIn className="w-4 h-4" /></>}
            </button>
          </form>

          <div className="mt-6 pt-5 border-t border-[#e6e0d4] text-center text-xs text-slate-400">
            🔒 Secure 256-bit encrypted session
          </div>
        </div>
      </div>
    </div>
  );
}
